import { AxiosError } from 'axios';
import { api, authOptions } from '../../utils/api';
import { ContractSigningKind, ContractSigningKindOptionsMap } from './constants';
import { getFirstBackendError } from './errors';
import { ContractSigningForm } from './schemas';

export interface UploadFilesForSignResponse {
  url: string;
  detail_url: string;
}

export type UploadFilesForSignError = AxiosError<{
  context: {
    status: number;
    data: {
      code: string;
      message: string;
      description: string;
      reason: string;
      timestamp: string;
      trace_id: string;
    };
  };
}>;

// Base endpoint for upload
const UPLOAD_FILES_FOR_SIGN_BASE_ENDPOINT = '/signer-session/';

/**
 * Upload files for signing process
 * @param payload - FormData containing all required fields and files
 * @param isMultipleSignProcess - Determines if the process is for multiple signers
 *   (juridical-person) or single (natural-person)
 * @returns Axios response with URLs
 */
export const uploadFilesForSign = async ({
  payload,
  contractSigningKind,
}: {
  payload: FormData;
  contractSigningKind: ContractSigningKind;
}) => {
  const isNaturalPerson = contractSigningKind === ContractSigningKindOptionsMap.NATURAL_PERSON;
  const endpoint = `${UPLOAD_FILES_FOR_SIGN_BASE_ENDPOINT}${isNaturalPerson ? 'natural-person/' : 'juridical-person/'}`;

  return api.post<UploadFilesForSignResponse>(endpoint, payload, {
    ...authOptions,
    headers: {
      ...authOptions.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

interface ValidationSuccessResponse {
  message: string;
  document_type: string;
  document_type_id: string;
}

export type ValidationErrorResponse = AxiosError<{
  message: string[];
  extended_errors: {
    reason: string;
    description: string;
  };

}>;

export const validateDocument = async (document: File, rut: ContractSigningForm['clientRUT']) => {
  const formData = new FormData();
  formData.append('file', document);
  formData.append('rut', rut);
  try {
    const response = await api.post<ValidationSuccessResponse>('/signer-session/verify-document/', formData, {
      ...authOptions,
      headers: {
        ...authOptions.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    const documentType = response.data.document_type;
    const documentTypeId = response.data.document_type_id;
    return {
      message: `Validamos este documento como ${documentType}.`,
      documentTypeId,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorData = getFirstBackendError(error.response?.data as Record<string, unknown>);
      const errorResponse = errorData;
      throw new Error(errorResponse?.extended_errors?.description || '');
    }

    throw new Error('Algo salió mal');
  }
};
